/**
 * Storage utility for selectedTeamId using localStorage for cross-tab sharing.
 * Replaces sessionStorage to enable team selection persistence across browser tabs,
 * windows, and sessions. This solves the issue where cmd+click or "Open in new tab"
 * actions lose the selected team context.
 *
 * Benefits:
 * - Cross-tab synchronization: All tabs/windows share the same team selection
 * - Session persistence: Team selection survives browser restarts
 * - Better UX: Users don't need to re-select teams in new tabs
 *
 * Security: Team ID is cleared on logout to prevent unauthorized access.
 */

const SELECTED_TEAM_ID_KEY = 'selectedTeamId';

/**
 * Save selected team ID to localStorage (shared across tabs and sessions)
 *
 * @param teamId - The team ID to store, or null to remove
 * @returns void
 *
 * @example
 * ```typescript
 * // Store a team ID
 * teamStorage.setTeamId(123);
 * teamStorage.setTeamId("456");
 *
 * // Clear team ID
 * teamStorage.setTeamId(null);
 * ```
 */
export function setTeamId(teamId: string | number | null | undefined): void {
  try {
    if (teamId === null || teamId === undefined) {
      localStorage.removeItem(SELECTED_TEAM_ID_KEY);
    } else {
      localStorage.setItem(SELECTED_TEAM_ID_KEY, String(teamId));
    }
  } catch (error) {
    console.error('Failed to save selectedTeamId to localStorage:', error);
  }
}

/**
 * Get selected team ID from localStorage
 *
 * @returns The stored team ID as a string, or null if not found
 *
 * @example
 * ```typescript
 * const teamId = teamStorage.getTeamId();
 * if (teamId) {
 *   // Use team ID
 *   dispatch(fetchTeamData(teamId));
 * }
 * ```
 */
export function getTeamId(): string | null {
  try {
    return localStorage.getItem(SELECTED_TEAM_ID_KEY);
  } catch (error) {
    console.error('Failed to get selectedTeamId from localStorage:', error);
    return null;
  }
}

/**
 * Clear selected team ID from localStorage.
 * Should be called during logout to prevent unauthorized access.
 *
 * @returns void
 *
 * @example
 * ```typescript
 * // Clear on logout
 * teamStorage.clearTeamId();
 * ```
 */
export function clearTeamId(): void {
  try {
    localStorage.removeItem(SELECTED_TEAM_ID_KEY);
  } catch (error) {
    console.error('Failed to clear selectedTeamId from localStorage:', error);
  }
}

/**
 * Check if a team ID is currently stored in localStorage
 *
 * @returns true if a team ID exists, false otherwise
 *
 * @example
 * ```typescript
 * if (teamStorage.hasTeamId()) {
 *   console.log('Team already selected');
 * }
 * ```
 */
export function hasTeamId(): boolean {
  try {
    return localStorage.getItem(SELECTED_TEAM_ID_KEY) !== null;
  } catch (error) {
    console.error('Failed to check localStorage for selectedTeamId:', error);
    return false;
  }
}

// Export as namespace for easier imports
export const teamStorage = {
  setTeamId,
  getTeamId,
  clearTeamId,
  hasTeamId,
};
